import React from "react";
import type { PayCashierMethodOption, PayCashierSessionView } from "./CashierType";
import { formatAmountYuan } from "./utils/device";
import { resolveCashierMediaUrl } from "./utils/resolveCashierMediaUrl";
import {
  CheckCircleIcon,
  CopyIcon,
  EnterprisePayIcon,
  ShieldCheckIcon,
  WechatPayIcon,
} from "./CashierIcons";
import { copyTextToClipboard } from "./utils/copyTextToClipboard";
import { showToast } from "./utils/toast";
import { CashierBrandLogo } from "./CashierBrandLogo";
import { CashierQrSlot } from "./CashierQrSlot";
import { CashierPayBlockedPanel } from "./CashierPayBlockedPanel";
import { CashierPreviewImage } from "./CashierPreviewImage";
import { buildCashierVisualStyle } from "./cashierVisualStyle";
import type { CashierExpiredPrompt } from "./cashierExpiredError";
import { CashierCountdown } from "./CashierCountdown";
import { formatCashierOrderTime } from "./cashierDateTime";
import {
  resolveCashierCreatedAt,
  resolveCashierProductName,
  resolveCashierProductSpec,
} from "./cashierSessionDisplay";
import "./CashierStyle.css";

export interface CashierViewProps {
  session: PayCashierSessionView;
  paying: boolean;
  paid: boolean;
  payBlocked?: CashierExpiredPrompt | null;
  wechatNativeCodeUrl?: string;
  onSelectMethod: (method: PayCashierMethodOption) => void;
  onBack?: () => void;
}

const PAY_SUPPORT_HINT = "如遇到支付问题，请联系圆领客服协助处理。";

export const CashierWebView: React.FC<CashierViewProps> = ({
  session,
  paying,
  paid,
  payBlocked,
  wechatNativeCodeUrl,
  onSelectMethod,
  onBack,
}) => {
  const methods = session.payMethods ?? [];
  const hasQr = Boolean(wechatNativeCodeUrl?.trim());
  const activeMethodCode = paid ? "" : hasQr || paying ? "wechat" : "";
  const productName = resolveCashierProductName(session);
  const productSpec = resolveCashierProductSpec(session);
  const productPosterUrl = resolveCashierMediaUrl(session.productThumbnailUrl);
  const hasProductPoster = Boolean(productPosterUrl);
  const createdAtText = resolveCashierCreatedAt(session);

  const handleCopyOrderId = () => {
    void copyTextToClipboard(session.bizOrderId).then((ok) => {
      if (ok) {
        showToast("已复制订单号");
        return;
      }
      showToast("复制失败，请重试");
    });
  };

  const renderOrderRow = (
    label: string,
    value: React.ReactNode,
    options?: { mono?: boolean; copyable?: boolean }
  ) => {
    const valueNode = options?.mono ? (
      <span className="cashier-web-mono">{value}</span>
    ) : (
      <span className="cashier-web-order-value">{value}</span>
    );

    return (
      <>
        <span className="cashier-web-label">{label}</span>
        {options?.copyable ? (
          <span className="cashier-web-order-value-wrap">
            {valueNode}
            <button
              type="button"
              className="cashier-web-copy-btn"
              aria-label="复制订单号"
              title="复制"
              onClick={handleCopyOrderId}
            >
              <CopyIcon size={14} />
            </button>
          </span>
        ) : (
          valueNode
        )}
      </>
    );
  };

  const renderOrderDetails = () => (
    <div className="cashier-web-order">
      <span className="cashier-web-label">商品</span>
      <span className="cashier-web-order-value">{productName}</span>

      {renderOrderRow("规格", productSpec || "—")}
      {renderOrderRow(
        "下单时间",
        createdAtText ? formatCashierOrderTime(createdAtText) : "—",
        { mono: true }
      )}
      {renderOrderRow("订单号", session.bizOrderId, { mono: true, copyable: true })}
      <span className="cashier-web-label">有效期</span>
      {createdAtText || session.expireAt ? (
        <CashierCountdown createdAt={createdAtText} expireAt={session.expireAt} />
      ) : (
        <span className="cashier-web-countdown">—</span>
      )}
    </div>
  );

  const renderPayPanel = () => (
    <div className="cashier-web-pay-panel">
      <p className="cashier-section-title cashier-section-title-web">选择支付方式</p>
      <div className="cashier-method-list-web">
        {methods.map((m) => {
          const isActive = !paid && m.code === activeMethodCode;
          return (
            <button
              key={m.code}
              type="button"
              className={
                !m.enabled
                  ? "cashier-method-tile cashier-method-tile-disabled"
                  : isActive
                    ? "cashier-method-tile cashier-method-tile-active"
                    : "cashier-method-tile"
              }
              disabled={paid || paying || !m.enabled || Boolean(payBlocked)}
              onClick={() => onSelectMethod(m)}
            >
              {m.code === "wechat" ? (
                <WechatPayIcon className="cashier-pay-method-icon" size={28} />
              ) : (
                <EnterprisePayIcon className="cashier-pay-method-icon" size={28} />
              )}
              <span className="cashier-method-tile-label">{m.label}</span>
              {!m.enabled ? <span className="cashier-method-tag">暂未开通</span> : null}
            </button>
          );
        })}
      </div>
      {paid ? (
        <div className="cashier-qr-placeholder">
          <div className="cashier-qr-placeholder-box cashier-qr-placeholder-box--paid">
            <CheckCircleIcon className="cashier-qr-paid-icon" size={40} />
            <span className="cashier-qr-paid-text">支付成功</span>
          </div>
          <p className="cashier-qr-placeholder-hint">支付已完成，页面将自动跳转</p>
        </div>
      ) : payBlocked ? (
        <CashierPayBlockedPanel prompt={payBlocked} onBack={onBack} />
      ) : (
        <CashierQrSlot codeUrl={wechatNativeCodeUrl} paying={paying} />
      )}
    </div>
  );

  return (
    <div
      className="cashier-shell cashier-shell-web"
      style={buildCashierVisualStyle()}
    >
      <div className="cashier-web-card">
        <div className="cashier-web-columns">
          <div className="cashier-web-pane cashier-web-pane-left">
            <header className="cashier-web-header">
              <div className="cashier-web-header-main">
                <div className="cashier-web-title-row">
                  <h1 className="cashier-web-title">
                    <CashierBrandLogo className="cashier-brand-logo" size={30} />
                    圆领收银台
                  </h1>
                </div>
                <div className="cashier-trust-row">
                  <span className="cashier-trust-badge">
                    <ShieldCheckIcon size={14} />
                    安全加密
                  </span>
                  <span className="cashier-trust-badge">
                    <CheckCircleIcon size={14} />
                    官方支付通道
                  </span>
                </div>
              </div>
            </header>

            {hasProductPoster ? (
              <div className="cashier-web-order-panel cashier-web-order-panel--with-poster">
                <div className="cashier-web-product-poster-card">
                  <CashierPreviewImage
                    className="cashier-web-product-poster"
                    src={productPosterUrl!}
                    alt={productName}
                    previewAriaLabel="查看商品大图"
                  />
                </div>
                <div className="cashier-web-order-body">{renderOrderDetails()}</div>
              </div>
            ) : (
              <div className="cashier-web-order-panel">{renderOrderDetails()}</div>
            )}

            <div className="cashier-web-amount-box">
              <span className="cashier-web-amount-label">应付金额</span>
              <span className="cashier-web-amount">
                ¥{formatAmountYuan(session.payAmountYuan)}
              </span>
            </div>
          </div>

          <div className="cashier-web-pane cashier-web-pane-right">{renderPayPanel()}</div>
        </div>
        <footer className="cashier-web-card-foot">
          <div className="cashier-web-card-foot-left">
            {onBack ? (
              <button type="button" className="cashier-web-back" onClick={onBack}>
                {"<< 返回商户"}
              </button>
            ) : null}
          </div>
          <div className="cashier-web-card-foot-right">
            <p className="cashier-web-card-support-hint">{PAY_SUPPORT_HINT}</p>
          </div>
        </footer>
      </div>
    </div>
  );
};
